// Layout constants and helpers for the editorial value-design spread.
// The CSS grid uses these exact pixel heights so the SVG connector lane can
// position curves with simple index math instead of runtime measurement.

export const ROW_H = 56
export const EYEBROW_H = 28
export const SECTION_GAP = 20
export const PAD_TOP = 20
export const IDENTITY_H = 50
export const IDENTITY_GAP = 14
export const LANE_W = 96

// 1-indexed row positions in the CSS grid:
// row 1 → top padding
// row 2 → identity (customer left / solution right)
// row 3 → identity gap
// row 4 → eyebrow (pains / relievers)
// rows 5..(5+maxPainRows-1) → pain / reliever item rows
// row (5+maxPainRows) → section gap
// row (6+maxPainRows) → eyebrow (outcomes / creators)
// rows (7+maxPainRows)..(7+maxPainRows+maxOutcomeRows-1) → outcome / creator rows
// row (7+maxPainRows+maxOutcomeRows) → bottom padding

export interface SpreadLayout {
  maxPainRows: number
  maxOutcomeRows: number
  /** 1-indexed grid-row for first pain/reliever item */
  painStartRow: number
  /** 1-indexed grid-row for first outcome/creator item */
  outcomeStartRow: number
  /** y position of each pain/reliever row centre, in svg coords */
  painY: (i: number) => number
  /** y position of each outcome/creator row centre */
  outcomeY: (i: number) => number
  /** Total height in pixels of the spread (= svg height) */
  totalH: number
  /** Grid template rows string for inline style */
  gridTemplateRows: string
}

export function computeLayout(
  painCount: number,
  relieverCount: number,
  outcomeCount: number,
  creatorCount: number,
): SpreadLayout {
  const maxPainRows    = Math.max(painCount,    relieverCount, 1)
  const maxOutcomeRows = Math.max(outcomeCount, creatorCount,  1)

  const painStartRow    = 5
  const outcomeStartRow = painStartRow + maxPainRows + 2 // + gap + eyebrow

  const yIdentityEnd  = PAD_TOP + IDENTITY_H + IDENTITY_GAP
  const yPainsStart   = yIdentityEnd + EYEBROW_H
  const painY         = (i: number) => yPainsStart + i * ROW_H + ROW_H / 2
  const yPainsEnd     = yPainsStart + maxPainRows * ROW_H

  const yOutStart     = yPainsEnd + SECTION_GAP + EYEBROW_H
  const outcomeY      = (i: number) => yOutStart + i * ROW_H + ROW_H / 2
  const yOutEnd       = yOutStart + maxOutcomeRows * ROW_H

  const totalH = yOutEnd + PAD_TOP

  const rows = [
    `${PAD_TOP}px`,
    `${IDENTITY_H}px`,
    `${IDENTITY_GAP}px`,
    `${EYEBROW_H}px`,
    ...Array(maxPainRows).fill(`${ROW_H}px`),
    `${SECTION_GAP}px`,
    `${EYEBROW_H}px`,
    ...Array(maxOutcomeRows).fill(`${ROW_H}px`),
    `${PAD_TOP}px`,
  ]

  return {
    maxPainRows,
    maxOutcomeRows,
    painStartRow,
    outcomeStartRow,
    painY,
    outcomeY,
    totalH,
    gridTemplateRows: rows.join(' '),
  }
}

// ===== Link maps (m:n) ===================================================

export interface PainItem    { id: string; description: string; severity?: string }
export interface OutcomeItem { id: string; description: string; importance?: string }

export interface RelieverItem { id?: string; description: string; painId?: string;  painIds?: string[] }
export interface CreatorItem  { id?: string; description: string; gainId?: string;  gainIds?: string[] }

/**
 * Returns:
 *   - byPain[i]      → array of reliever indices that link to pains[i]
 *   - byReliever[j]  → array of pain indices that reliever[j] tackles
 */
export function buildPainReliefLinks(
  pains: PainItem[],
  relievers: RelieverItem[],
): { byPain: number[][]; byReliever: number[][] } {
  const painIndex = new Map<string, number>()
  pains.forEach((p, i) => painIndex.set(p.id, i))

  const byPain: number[][] = pains.map(() => [])
  const byReliever: number[][] = relievers.map(() => [])

  relievers.forEach((r, j) => {
    const ids = (r.painIds && r.painIds.length > 0) ? r.painIds : (r.painId ? [r.painId] : [])
    ids.forEach(pid => {
      const i = painIndex.get(pid)
      if (i === undefined) return
      byReliever[j].push(i)
      byPain[i].push(j)
    })
  })

  return { byPain, byReliever }
}

export function buildOutcomeCreatorLinks(
  outcomes: OutcomeItem[],
  creators: CreatorItem[],
): { byOutcome: number[][]; byCreator: number[][] } {
  const outIndex = new Map<string, number>()
  outcomes.forEach((o, i) => outIndex.set(o.id, i))

  const byOutcome: number[][] = outcomes.map(() => [])
  const byCreator: number[][] = creators.map(() => [])

  creators.forEach((c, j) => {
    const ids = (c.gainIds && c.gainIds.length > 0) ? c.gainIds : (c.gainId ? [c.gainId] : [])
    ids.forEach(gid => {
      const i = outIndex.get(gid)
      if (i === undefined) return
      byCreator[j].push(i)
      byOutcome[i].push(j)
    })
  })

  return { byOutcome, byCreator }
}

// ===== ID labels =========================================================

export const painLabel     = (i: number) => `P${i + 1}`
export const reliefLabel   = (i: number) => `R${i + 1}`
export const outcomeLabel  = (i: number) => `O${i + 1}`
export const creatorLabel  = (i: number) => `G${i + 1}`
